import { Constants } from "appConstants";
import { cnpj, cpf } from "cpf-cnpj-validator";
import * as Yup from "yup";
import { AnyObject } from "yup/lib/types";
import { ApplicantDataFields, ApplicationFormik, ApplicationVehicleFields } from "../types";

import ApplicantAddressSection from "./ApplicantAddressSection";
import ApplicantDataSection from "./ApplicantDataSection";
import ApplicationVehicleSection from "./ApplicationVehicleSection";

export const applicationDataSections = {
	applicantData: "applicantData",
	applicantAddress: "applicantAddress",
	applicationVehicle: "applicationVehicle"
};

export const applicationDataSectionsFields = {
	[applicationDataSections.applicantData]: [
		ApplicantDataFields.applicantName,
		ApplicantDataFields.applicantDocument,
		ApplicantDataFields.applicantPhoneNumber,
		ApplicantDataFields.applicantEmail
	],
	[applicationDataSections.applicantAddress]: [
		ApplicantDataFields.applicantAddressPostalCode,
		ApplicantDataFields.applicantAddressStreet,
		ApplicantDataFields.applicantAddressNumber,
		ApplicantDataFields.applicantAddressComplement,
		ApplicantDataFields.applicantAddressNeighbourhood,
		ApplicantDataFields.applicantAddressState,
		ApplicantDataFields.applicantAddressCity
	],
	[applicationDataSections.applicationVehicle]: [
		ApplicationVehicleFields.plate,
		ApplicationVehicleFields.renavamCode,
		ApplicationVehicleFields.numberOfAxis,
		ApplicationVehicleFields.vehicleBrand,
		ApplicationVehicleFields.vehicleModel,
		ApplicationVehicleFields.color,
		ApplicationVehicleFields.manufactureYear,
		ApplicationVehicleFields.modelYear,
		ApplicationVehicleFields.tagNumber
	]
};

const errorMessages = Constants.ErrorMessages;

function mustNotBePlaceholderDocument(this: Yup.TestContext<AnyObject>, val: string | undefined | null): boolean {
	const isJuridicalPerson = val?.length && val?.length > 11;
	if (isJuridicalPerson) {
		return cnpj.isValid(val || "");
	}
	return cpf.isValid(val || "");
}

export const applicantDataSchema = Yup.object().shape({
	applicantName: Yup.string().nullable().required(errorMessages.requiredField),
	applicantDocument: Yup.string()
		.nullable()
		.required(errorMessages.requiredField)
		.test("mustNotBePlaceholderDocument", errorMessages.mustNotBePlaceholderDocument, mustNotBePlaceholderDocument),
	applicantPhoneNumber: Yup.string()
		.nullable()
		.test("len", "É necessário preencher ao menos 10 dígitos", (val) => !!val?.length && val.length >= 10)
		.required(errorMessages.requiredField),
	applicantEmail: Yup.string().nullable().email(errorMessages.validEmail).required(errorMessages.requiredField),

	applicantAddressPostalCode: Yup.string()
		.nullable()
		.min(9, "Campo deve ter no mínimo 9 dígitos.")
		.max(9, "Campo deve ter no máximo 9 dígitos.")
		.required(errorMessages.requiredField),
	applicantAddressStreet: Yup.string().nullable().required(errorMessages.requiredField),
	applicantAddressNumber: Yup.string().nullable().required(errorMessages.requiredField),
	applicantAddressComplement: Yup.string().nullable(),
	applicantAddressNeighbourhood: Yup.string().nullable().required(errorMessages.requiredField),
	applicantAddressState: Yup.string().nullable().required(errorMessages.requiredField),
	applicantAddressCity: Yup.string().nullable().required(errorMessages.requiredField)
});

const tagDependencyTest = (fieldToCheck: string) =>
	function validateTagDependency(this: Yup.TestContext, value: any) {
		const relatedFieldValue = this.parent[fieldToCheck];
		if (relatedFieldValue && relatedFieldValue !== "") {
			return !!value;
		}
		return true;
	};

export const applicationVehicleSchema = Yup.object().shape({
	plate: Yup.string()
		.nullable()
		.min(7, "Campo deve ter no mínimo 7 dígitos.")
		.max(7, "Campo deve ter no máximo 7 dígitos.")
		.required(errorMessages.requiredField),
	renavamCode: Yup.string()
		.nullable()
		.min(11, "Campo deve ter no mínimo 11 dígitos.")
		.max(11, "Campo deve ter no máximo 11 dígitos.")
		.required(errorMessages.requiredField),
	vehicleModel: Yup.string().nullable().required(errorMessages.requiredField),
	color: Yup.string().nullable().required(errorMessages.requiredField),
	manufactureYear: Yup.number()
		.nullable()
		.required(errorMessages.requiredField)
		.test(
			"manufactureYearAfterModelYear",
			"A diferença entre ano de fabricação e ano do modelo deve ser de no máximo 1 ano",
			(val, context) => !!(val && val >= context.parent.modelYear - 1)
		),
	modelYear: Yup.number().nullable().required(errorMessages.requiredField),
	tagNumber: Yup.string()
		.nullable()
		.test("tagNumber-required", "Campo obrigatório quando empresa responsável está preenchida", tagDependencyTest("tagCompany"))
		.when("tagSelection", {
			is: (selection: string | null) => selection === "yes",
			then: (schema) => schema.required(errorMessages.requiredField),
			otherwise: (schema) => schema
		})
		.max(10, "Campo deve ter no máximo 10 dígitos."),
	tagCompany: Yup.object()
		.nullable()
		.test("tagCompany-required", "Campo obrigatório quando número da TAG está preenchido", tagDependencyTest("tagNumber"))
		.when("tagSelection", {
			is: (selection: string | null) => selection === "yes",
			then: (schema) => schema.required(errorMessages.requiredField),
			otherwise: (schema) => schema
		}),
	tagSelection: Yup.string()
		.nullable()
		.when("foundPlateTagsCount", (count: number, schema: any) => {
			if (count && count > 0) {
				return schema.required("Escolha uma opção para prosseguir");
			}
			return schema;
		}),
	foundPlateTagsCount: Yup.number().nullable().default(0)
});

export default function ApplicationDataStep({ formik }: Readonly<ApplicationFormik>): JSX.Element {
	return (
		<div className="flex flex-col overflow-y-auto p-8 gap-8 h-full max-w-2xl">
			<ApplicantDataSection formik={formik} />
			<ApplicantAddressSection formik={formik} />
			<ApplicationVehicleSection formik={formik} />
		</div>
	);
}
